import getEnvironmentConfiguration from '@/config'
import axios from 'axios'

// ---------------------------------------------------------------------------
// SECURITY NOTE — read before modifying.
//
// The interceptor installed below is CLIENT-SIDE ONLY. It does not create an
// endpoint, relax authorization, or grant any access. It reacts to a 401 the
// server has ALREADY returned; the server remains the sole authority on auth
// and rejects unauthorized requests exactly as it did before this existed.
//
// The session-expired modal it triggers is a USABILITY guard, not a security
// boundary. Removing that modal via devtools yields nothing — the request
// still 401s server-side. NEVER treat this, or any client-side check, as
// protection for data. Authorization lives in the API.
//
// WHY IT EXISTS (sc-160): expiry was already detected (Auth.service polls
// every 20s) but nothing blocked interaction, so a user whose session had
// died could "delete" mail, watch it vanish from the UI, and have every call
// 401 into a void. This is the backstop for calls that 401 inside that 20s
// detection window.
//
// GATE: it fires only when the store believed the user was logged in
// (me.logged === true). Public onboarding/billing endpoints use this same
// client while logged out and must never trigger a login prompt.
//
// NO LOOP RISK: Amplify auth (Auth.signIn / Auth.currentSession) talks to
// Cognito directly and does NOT pass through this axios instance, so
// re-authentication cannot recurse back through here.
// ---------------------------------------------------------------------------
function installUnauthorizedInterceptor (axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        // Lazy require: this module is imported very early, and pulling the
        // store in at module scope creates a cycle.
        // eslint-disable-next-line global-require
        const store = require('@/core/store').default
        if (store.getState()?.me?.logged) {
          store.commit('TOPBAR:USER_CREDENTIALS_EXPIRED')
        }
      }
      return Promise.reject(error)
    }
  )
}

export class APIClient {
  constructor () {
    const config = getEnvironmentConfiguration()
    this.API_URL = config.REACT_APP_API_URL || 'https://api.boxes-dev.link'
    if (process.env.REACT_APP_TEST_API) { this.API_URL = 'http://localhost:5000' }
    this.axios = axios
    this.logged = false
    installUnauthorizedInterceptor(this.axios)
  }

  get (url) {
    return new Promise((resolve, reject) => {
      this.axios.get(`${this.API_URL}/${url}`)
        .then(response => resolve(response))
        .catch(error => reject(error.response))
    })
  }

  post (url, params) {
    return new Promise((resolve, reject) => {
      this.axios.post(`${this.API_URL}/${url}`, params)
        .then(response => resolve(response))
        .catch(error => reject(error.response))
    })
  }

  put (url, params) {
    return new Promise((resolve, reject) => {
      this.axios.put(`${this.API_URL}/${url}`, params)
        .then(response => resolve(response))
        .catch(error => reject(error.response))
    })
  }

  delete (url) {
    return new Promise((resolve, reject) => {
      this.axios.delete(`${this.API_URL}/${url}`)
        .then(response => resolve(response))
        .catch(error => reject(error.response))
    })
  }

  setUpUserIdHeader (userId) {
    this.axios.defaults.headers['X-User-ID'] = userId
  }

  controlStatusFnct (res, error) {
    if (res.status === 401 && !(process.env.TEST_API || process.env.TEST)) {
      window.localStorage.clear()
      window.location.reload()
    } else {
      throw new Error(error)
    }
  }
}

export const API = new APIClient()
